import { useState, useEffect, useRef } from "react";

// ═══════════════════════════════════════════════════════════════
// HAVEN PANEL — Your personal island. Zero productivity pressure.
// 8 spaces: Lighthouse, Observatory, Grove, Cafe, Rec Room,
//           Workshop, Library, Gray Havens
// ═══════════════════════════════════════════════════════════════

// ── Grove: Breathe ────────────────────────────────────────────
const BREATHE_PATTERNS = [
  { id: "box",     label: "Box Breathing",    steps: [{ label: "Inhale",  secs: 4 }, { label: "Hold",    secs: 4 }, { label: "Exhale",  secs: 4 }, { label: "Hold",    secs: 4 }] },
  { id: "478",     label: "4-7-8",            steps: [{ label: "Inhale",  secs: 4 }, { label: "Hold",    secs: 7 }, { label: "Exhale",  secs: 8 }] },
  { id: "calm",    label: "Calm Down",        steps: [{ label: "Inhale",  secs: 4 }, { label: "Exhale",  secs: 6 }] },
];

function BreatheMode({ onBack }) {
  const [pattern, setPattern] = useState(BREATHE_PATTERNS[0]);
  const [running, setRunning] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [secsLeft, setSecsLeft] = useState(BREATHE_PATTERNS[0].steps[0].secs);
  const [cycles, setCycles] = useState(0);
  const intervalRef = useRef(null);

  const stop = () => {
    clearInterval(intervalRef.current);
    setRunning(false);
    setStepIdx(0);
    setSecsLeft(pattern.steps[0].secs);
  };

  const start = () => {
    setStepIdx(0);
    setSecsLeft(pattern.steps[0].secs);
    setCycles(0);
    setRunning(true);
  };

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecsLeft(s => {
        if (s > 1) return s - 1;
        setStepIdx(prev => {
          const next = (prev + 1) % pattern.steps.length;
          if (next === 0) setCycles(c => c + 1);
          setSecsLeft(pattern.steps[next].secs);
          return next;
        });
        return pattern.steps[0].secs;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running, pattern]);

  const currentStep = pattern.steps[stepIdx];
  const progress = running ? 1 - (secsLeft / currentStep.secs) : 0;
  const circumference = 2 * Math.PI * 54;

  return (
    <div className="flex flex-col items-center px-5 py-6 gap-5">
      {/* Pattern picker */}
      <div className="flex gap-2 flex-wrap justify-center">
        {BREATHE_PATTERNS.map(p => (
          <button key={p.id} onClick={() => { setPattern(p); stop(); }}
            className="py-[5px] px-3.5 rounded-[20px] border-none cursor-pointer text-xs font-semibold font-['DM_Sans'] transition-all duration-150"
            style={{ background: pattern.id === p.id ? "#4DB8B5" : "#F0EDE8", color: pattern.id === p.id ? "white" : "#5A6A7A" }}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Circle */}
      <div className="relative w-[140px] h-[140px]">
        <svg width="140" height="140" className="absolute inset-0 -rotate-90">
          <circle cx="70" cy="70" r="54" fill="none" stroke="rgba(77,184,181,0.15)" strokeWidth="8" />
          <circle cx="70" cy="70" r="54" fill="none" stroke="#4DB8B5" strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - progress)}
            strokeLinecap="round"
            style={{ transition: running ? "stroke-dashoffset 1s linear" : "none" }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {running ? (
            <>
              <div className="text-[13px] font-bold text-[#4DB8B5] uppercase tracking-[0.08em]">{currentStep.label}</div>
              <div className="text-[32px] font-bold text-slate-800 leading-none">{secsLeft}</div>
              {cycles > 0 && <div className="text-[10px] text-slate-400 mt-0.5">{cycles} {cycles === 1 ? "cycle" : "cycles"}</div>}
            </>
          ) : (
            <div className="text-[13px] text-slate-400 text-center leading-[1.5]">Ready<br/>when you are</div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-2.5">
        {!running
          ? <button onClick={start} className="py-2.5 px-7 rounded-xl border-none bg-[#4DB8B5] text-white text-sm font-bold cursor-pointer font-['DM_Sans']">Start</button>
          : <button onClick={stop} className="py-2.5 px-7 rounded-xl border-none bg-[#F0EDE8] text-[#5A6A7A] text-sm font-semibold cursor-pointer font-['DM_Sans']">Stop</button>
        }
      </div>

      <div className="text-[11px] text-slate-400 text-center max-w-[240px] leading-[1.6]">
        {pattern.steps.map(s => `${s.label} ${s.secs}s`).join(" · ")}
      </div>
    </div>
  );
}

// ── Gray Havens ───────────────────────────────────────────────
const GRAY_HAVEN_STATES = {
  completed: { label: "Completed", color: "#22C55E", bg: "rgba(34,197,94,0.08)", desc: "Done. Finished. Proud.", icon: "✦" },
  sunsetted: { label: "Sunsetted",  color: "#94A3B8", bg: "rgba(148,163,184,0.08)", desc: "Paused or cancelled. No shame.", icon: "◐" },
  archived:  { label: "Archived",   color: "#F59E0B", bg: "rgba(245,158,11,0.08)", desc: "Not active, not gone.", icon: "⊙" },
};

function GrayHavensMode() {
  const [filter, setFilter] = useState("all");
  // Placeholder items - in production these come from Supabase world data
  const items = [
    { id: 1, name: "Q4 Brand Refresh", type: "building", state: "completed", island: "Work Island" },
    { id: 2, name: "Mobile App Concept", type: "island", state: "sunsetted", island: "Side Projects" },
    { id: 3, name: "Old Client Pitch", type: "building", state: "archived", island: "Work Island" },
    { id: 4, name: "Podcast Project", type: "building", state: "sunsetted", island: "Creative" },
  ];

  const filtered = filter === "all" ? items : items.filter(i => i.state === filter);

  return (
    <div className="px-5 py-4 flex flex-col gap-3.5">
      <div className="text-[13px] text-slate-500 leading-[1.6]">
        Nothing is ever gone. Everything here is recoverable.
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1.5">
        {["all", "completed", "sunsetted", "archived"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className="py-1 px-3 rounded-[20px] border-none cursor-pointer text-[11px] font-semibold font-['DM_Sans'] capitalize transition-all duration-150"
            style={{ background: filter === f ? "#1E293B" : "#F0EDE8", color: filter === f ? "white" : "#64748B" }}>
            {f}
          </button>
        ))}
      </div>

      {/* Items */}
      <div className="flex flex-col gap-1.5">
        {filtered.map(item => {
          const s = GRAY_HAVEN_STATES[item.state];
          return (
            <div key={item.id} className="flex items-center gap-3 py-2.5 px-3.5 rounded-[10px]"
              style={{ background: s.bg, border: `1px solid ${s.color}22` }}>
              <span className="text-base" style={{ color: s.color }}>{s.icon}</span>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-slate-800">{item.name}</div>
                <div className="text-[10px] text-slate-400 mt-px">{item.island} · {item.type}</div>
              </div>
              <span className="text-[10px] font-semibold py-0.5 px-2 rounded-[10px]"
                style={{ color: s.color, background: `${s.color}18` }}>{s.label}</span>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-6 text-slate-400 text-[13px]">Nothing here yet.</div>
        )}
      </div>
    </div>
  );
}

// ── Placeholder space ─────────────────────────────────────────
function ComingSoonMode({ space }) {
  const details = {
    observatory: { icon: "🔭", title: "The Observatory",  body: "Your behavioral patterns, made visible. Session data, journaling, weekly reports. Coming soon." },
    cafe:        { icon: "☕", title: "The Cafe",          body: "Body doubling with real users. Low-pressure social hangouts. Community sessions. Coming soon." },
    rec_room:    { icon: "🎮", title: "The Rec Room",      body: "Games with other users. Watch content together. The casual, looser version of the Cafe. Coming soon." },
    workshop:    { icon: "🔨", title: "The Workshop",      body: "Build and sell custom spaces, room templates, world assets. The creator economy layer. Coming soon." },
    library:     { icon: "📚", title: "The Library",       body: "Courses, skill-building resources, and educational content for neurodivergent professionals. Coming soon." },
  };
  const d = details[space] || { icon: "🏝", title: space, body: "Coming soon." };

  return (
    <div className="flex flex-col items-center justify-center px-8 py-12 text-center gap-3.5">
      <div className="text-5xl">{d.icon}</div>
      <div className="text-lg font-bold text-slate-800">{d.title}</div>
      <div className="text-[13px] text-slate-500 leading-[1.7] max-w-[280px]">{d.body}</div>
      <div className="py-1.5 px-4 rounded-[20px] text-[11px] font-semibold"
        style={{ background: "rgba(77,184,181,0.08)", border: "1px solid rgba(77,184,181,0.2)", color: "#4DB8B5" }}>Wave 2</div>
    </div>
  );
}

// ── Lighthouse (settings/overview) ───────────────────────────
function LighthouseMode({ onBrainDump, onClose }) {
  return (
    <div className="px-5 py-4 flex flex-col gap-2.5">
      <div className="text-[13px] text-slate-500 leading-[1.6] mb-1">
        Mission control for your entire world. Your settings, your avatar, your overview.
      </div>

      {[
        { icon: "🧠", label: "Retake Onboarding Quiz",   sub: "Update how Lumen knows you",           action: "quiz"       },
        { icon: "🌍", label: "Build Your World",          sub: "Brain Dump or add manually",           action: "braindump"  },
        { icon: "🎨", label: "Edit Avatar",               sub: "Coming soon — in profile settings now", action: null         },
        { icon: "⚙️", label: "World Settings",            sub: "Rename, reorganize, reset",             action: null         },
      ].map(item => (
        <div key={item.label}
          onClick={() => {
            if (item.action === "braindump") { onBrainDump(); onClose(); }
          }}
          className="flex items-center gap-3 py-2.5 px-3.5 rounded-[10px] bg-[#F8F6F2] border border-[#F0EDE8] transition-[background] duration-[120ms]"
          style={{ cursor: item.action ? "pointer" : "default", opacity: item.action ? 1 : 0.6 }}
          onMouseEnter={e => { if (item.action) e.currentTarget.style.background = "#F0EDE8"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#F8F6F2"; }}>
          <span className="text-lg">{item.icon}</span>
          <div>
            <div className="text-[13px] font-semibold text-slate-800">{item.label}</div>
            <div className="text-[10px] text-slate-400 mt-px">{item.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Grove hub ─────────────────────────────────────────────────
function GroveMode() {
  const [view, setView] = useState("hub"); // hub | breathe | meditate | ambient

  if (view === "breathe") {
    return (
      <div>
        <button onClick={() => setView("hub")} className="flex items-center gap-1.5 px-5 py-3 bg-none border-none cursor-pointer text-slate-400 text-xs font-['DM_Sans']">← Back</button>
        <BreatheMode onBack={() => setView("hub")} />
      </div>
    );
  }

  const options = [
    { id: "breathe",  icon: "🫁", label: "Breathe",         sub: "Box, 4-7-8, calm down patterns", available: true  },
    { id: "meditate", icon: "🧘", label: "Meditate",         sub: "2-min guided sessions",          available: false },
    { id: "ambient",  icon: "🎧", label: "Ambient Sounds",   sub: "Forest, rain, ocean, white noise", available: false },
    { id: "walk",     icon: "🚶", label: "AI Assisted Walk", sub: "Lumen guides you through a path", available: false },
  ];

  return (
    <div className="px-5 py-4 flex flex-col gap-2">
      <div className="text-[13px] text-slate-500 leading-[1.6] mb-1">
        Nature, calm, and recovery. No tasks. Nothing expected of you.
      </div>
      {options.map(opt => (
        <div key={opt.id}
          onClick={() => opt.available && setView(opt.id)}
          className="flex items-center gap-3 py-3 px-3.5 rounded-[10px] bg-[#F8F6F2] border border-[#F0EDE8] transition-[background] duration-[120ms]"
          style={{ cursor: opt.available ? "pointer" : "default", opacity: opt.available ? 1 : 0.55 }}
          onMouseEnter={e => { if (opt.available) e.currentTarget.style.background = "#F0EDE8"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "#F8F6F2"; }}>
          <span className="text-[22px]">{opt.icon}</span>
          <div className="flex-1">
            <div className="text-[13px] font-semibold text-slate-800">{opt.label}</div>
            <div className="text-[10px] text-slate-400 mt-px">{opt.sub}</div>
          </div>
          {opt.available
            ? <span className="text-[10px] text-[#4DB8B5]">→</span>
            : <span className="text-[9px] text-slate-300 italic">soon</span>
          }
        </div>
      ))}
    </div>
  );
}

// ── Space config ──────────────────────────────────────────────
const SPACES = {
  lighthouse:  { icon: "🔆", label: "The Lighthouse"  },
  observatory: { icon: "🔭", label: "The Observatory"  },
  grove:       { icon: "🌿", label: "The Grove"        },
  cafe:        { icon: "☕", label: "The Cafe"          },
  rec_room:    { icon: "🎮", label: "The Rec Room"      },
  workshop:    { icon: "🔨", label: "The Workshop"      },
  library:     { icon: "📚", label: "The Library"       },
  lab:         { icon: "🧪", label: "The Lab"           },
  gray_havens: { icon: "🌅", label: "Gray Havens"       },
};

// ── Main HavenPanel ───────────────────────────────────────────
export default function HavenPanel({ space, onClose, onBrainDump, onSpaceSelect }) {
  const [activeSpace, setActiveSpace] = useState(space || "lighthouse");
  const cfg = SPACES[activeSpace] || SPACES.lighthouse;

  const renderContent = () => {
    switch (activeSpace) {
      case "lighthouse":  return <LighthouseMode onBrainDump={onBrainDump} onClose={onClose} />;
      case "grove":       return <GroveMode />;
      case "gray_havens": return <GrayHavensMode />;
      default:            return <ComingSoonMode space={activeSpace} />;
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center font-['DM_Sans']" style={{ background: "rgba(15,23,42,0.75)", backdropFilter: "blur(8px)" }}>
      <div className="w-[min(760px,96vw)] h-[min(580px,92vh)] bg-[#FAFAF8] rounded-[20px] flex overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.4)]">

        {/* Left sidebar — space nav */}
        <div className="w-[180px] flex flex-col flex-shrink-0" style={{ background: "linear-gradient(180deg, #1E293B 0%, #2A3548 100%)" }}>
          {/* Haven header */}
          <div className="px-4 pt-5 pb-3.5">
            <div className="text-[11px] font-bold text-[#4DB8B5] tracking-[0.1em] uppercase mb-0.5">🏝 Haven</div>
            <div className="text-[10px] text-white/30">Your personal island</div>
          </div>

          {/* Space list */}
          <div className="flex-1 overflow-y-auto px-2">
            {Object.entries(SPACES).map(([id, cfg]) => {
              const isActive = activeSpace === id;
              const isAvailable = ["lighthouse", "grove", "gray_havens"].includes(id);
              return (
                <button key={id} onClick={() => (onSpaceSelect ? onSpaceSelect(id) : setActiveSpace(id))}
                  className="w-full flex items-center gap-2 py-2 px-2.5 rounded-lg border-none cursor-pointer mb-0.5 transition-[background] duration-[120ms]"
                  style={{ background: isActive ? "rgba(77,184,181,0.2)" : "transparent", opacity: isAvailable ? 1 : 0.45 }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
                  <span className="text-sm">{cfg.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="text-[11px] leading-[1.3]"
                      style={{ fontWeight: isActive ? 700 : 500, color: isActive ? "#4DB8B5" : "rgba(255,255,255,0.7)" }}>{cfg.label}</div>
                    {!isAvailable && <div className="text-[8px] text-white/20 mt-px">Wave 2</div>}
                  </div>
                  {isActive && <span className="w-1 h-1 rounded-full bg-[#4DB8B5] flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Close */}
          <div className="px-4 py-3 border-t border-white/[0.06]">
            <button onClick={onClose}
              className="w-full py-2 rounded-lg border border-white/10 bg-transparent text-white/40 text-[11px] cursor-pointer font-['DM_Sans'] transition-all duration-150"
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "rgba(255,255,255,0.7)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.4)"; }}>
              ← Leave Haven
            </button>
          </div>
        </div>

        {/* Right content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="px-6 pt-[18px] pb-3.5 border-b border-[#F0EDE8] flex items-center gap-2.5 flex-shrink-0">
            <span className="text-[22px]">{cfg.icon}</span>
            <div>
              <div className="text-[17px] font-bold text-slate-800">{cfg.label}</div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
